import {
  computed,
  onBeforeUnmount,
  ref,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue';
import { normalizeCalendarNoteContent } from './calendar-note-content';

const SAVE_DEBOUNCE_MS = 1000;
type SavedNote = { id: string; note: string };
type SaveState = 'idle' | 'saving' | 'saved' | 'error';

type CalendarNoteEditorOptions<TNote extends SavedNote> = {
  initialText: () => string;
  initialId: () => string | null | undefined;
  canWrite: (id: string | undefined) => boolean;
  create: (text: string) => Promise<TNote>;
  update: (id: string, text: string) => Promise<TNote>;
  remove: (id: string) => Promise<void>;
  onSaved?: (note: TNote, created: boolean) => void;
  onRemoved?: () => void;
  onError: (error: unknown) => void;
};

type CalendarNoteEditor = {
  text: Ref<string>;
  canWrite: ComputedRef<boolean>;
  saveState: Ref<SaveState>;
  save: () => Promise<void>;
};

/** One serialized create/update/delete queue, shared by both primary-note editors. */
export const useCalendarNoteEditor = <TNote extends SavedNote>(
  options: CalendarNoteEditorOptions<TNote>,
): CalendarNoteEditor => {
  const text = ref(options.initialText());
  const noteId = ref(options.initialId() ?? undefined);
  const saveState = ref<SaveState>('idle');
  const canWrite = computed(() => options.canWrite(noteId.value));
  let savedText = normalizeCalendarNoteContent(text.value);
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pending: Promise<void> | undefined;
  let syncing = false;
  const isDirty = (): boolean =>
    normalizeCalendarNoteContent(text.value) !== savedText;
  const cancelTimer = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };

  const drain = async (): Promise<void> => {
    try {
      while (canWrite.value && isDirty()) {
        const value = normalizeCalendarNoteContent(text.value);
        const id = noteId.value;
        saveState.value = 'saving';
        if (value === '') {
          if (id) await options.remove(id);
          noteId.value = undefined;
          savedText = value;
          if (id) options.onRemoved?.();
        } else {
          const note = id
            ? await options.update(id, value)
            : await options.create(value);
          noteId.value = note.id;
          savedText = value;
          options.onSaved?.(note, !id);
        }
      }
      saveState.value = isDirty() ? 'idle' : 'saved';
    } catch (error) {
      cancelTimer();
      saveState.value = 'error';
      options.onError(error);
    }
  };

  const save = (): Promise<void> => {
    cancelTimer();
    // Establish ownership before any API call can synchronously update props.
    pending ??= Promise.resolve()
      .then(drain)
      .finally(() => {
        pending = undefined;
      });
    return pending;
  };
  const schedule = (): void => {
    cancelTimer();
    if (pending || !isDirty()) return;
    saveState.value = 'idle';
    timer = setTimeout(() => {
      void save();
    }, SAVE_DEBOUNCE_MS);
  };

  watch(
    text,
    () => {
      if (!syncing) schedule();
    },
    { flush: 'sync' },
  );
  watch(
    () => [options.initialText(), options.initialId()] as const,
    ([nextText, nextId]) => {
      // Responses from requests in this queue cannot replace newer local edits.
      if (pending) return;
      const replaceText = !isDirty();
      syncing = true;
      if (replaceText) text.value = nextText;
      noteId.value = nextId ?? undefined;
      savedText = normalizeCalendarNoteContent(nextText);
      syncing = false;
      if (!isDirty()) saveState.value = 'idle';
      schedule();
    },
  );
  onBeforeUnmount(() => {
    cancelTimer();
    if (isDirty()) void save();
  });

  return { text, canWrite, saveState, save };
};
